import { useEffect, useState } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';
export type Resolved = 'light' | 'dark';

const STORAGE_KEY = 'roleforge-theme-pref';

function readPref(): ThemePref {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function systemResolved(): Resolved {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolve(pref: ThemePref): Resolved {
  return pref === 'system' ? systemResolved() : pref;
}

function apply(pref: ThemePref) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolve(pref));
}

export function useTheme() {
  const [pref, setPrefState] = useState<ThemePref>(() => readPref());

  useEffect(() => {
    apply(pref);
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, pref);
  }, [pref]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => { if (readPref() === 'system') apply('system'); };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return { pref, setPref: setPrefState };
}

export function initTheme() {
  apply(readPref());
}
